import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import CustomCalendar from '@/components/CustomComponent/CustomCalendar/CustomCalendar';
import { COMMON_TEXT } from '@/helpers/common-text';
import {
  convertArrayToObject,
  sortObjectKeys,
} from '@/helpers/utility';
import { format, isSameDay, isBefore } from 'date-fns';
import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { cloneDeep } from 'lodash';
import { fetchDataHistory, fetchDataHistoryEmptyEndDate } from '../query-request';
import fileService from '@/services/file-service';


@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class DeviceStatusOverviewHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dataTableHistory: this.props.dataTableHistory ?? [],
      isLoadingHistory: this.props.isLoadingHistory ?? false,
      startDate: this.props.startDate ?? new Date(),
      endDate: this.props.endDate ?? new Date(),
      floor: this.props.floor ?? null,
      selectedDevice: {},
    };
    this.overlayRef = React.createRef();
    this.dataHistoryTableRef = React.createRef();
    this.filters = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      product: { value: null, matchMode: FilterMatchMode.CONTAINS },
      device_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      device_name: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.dataTableHistory !== this.props.dataTableHistory) {
      this.setState({ dataTableHistory: this.props.dataTableHistory });
    }
    if (prevProps.isLoadingHistory !== this.props.isLoadingHistory) {
      this.setState({ isLoadingHistory: this.props.isLoadingHistory });
    }
    if (prevProps.startDate !== this.props.startDate) {
      this.setState({ startDate: this.props.startDate });
    }
    if (prevProps.endDate !== this.props.endDate) {
      this.setState({ endDate: this.props.endDate });
    }
    if (prevProps.floor !== this.props.floor) {
      this.setState({ floor: this.props.floor });
    }
  }

  datePickerOnChanged = (e, type) => {
    const { startDate, endDate } = this.state;
    let newVal = e.value;
    let updatedState = {};

    if (type === 'startDate') {
      if (newVal && endDate && isBefore(endDate, newVal)) {
        let newEndDate = new Date(newVal.getTime());
        newEndDate.setHours(23, 59, 59, 999);
        updatedState = { startDate: newVal, endDate: newEndDate };
      } else {
        updatedState = { startDate: newVal };
      }
    } else if (type === 'endDate') {
      let newEndDate = new Date(newVal.getTime());
      newEndDate.setHours(23, 59, 59, 999);
      if (startDate && newVal && isBefore(newVal, startDate)) {
        updatedState = { startDate: newVal, endDate: newEndDate };
      } else {
        updatedState = { endDate: newEndDate };
      }
    }

    this.props.onDateChange(updatedState.startDate, updatedState.endDate);

    this.setState(updatedState, () => {
      this.fetchDataHistoryByFloorId();
    });
  };

  fetchDataHistoryByFloorId = async () => {
    const { floor, startDate, endDate } = this.state;
    if (!floor) return;

    this.setState({ isLoadingHistory: true });

    let dataHistoryRes = await fetchDataHistory({
      floorId: floor,
      fromDate: format(startDate, 'yyyy-MM-dd 00:00:00'),
      toDate: format(endDate, 'yyyy-MM-dd 23:59:59'),
    });

    dataHistoryRes = dataHistoryRes ?? [];

    let dataHistoryEmptyEndDateRes = await fetchDataHistoryEmptyEndDate({
      floorId: floor,
      fromDate: format(startDate, 'yyyy-MM-dd 00:00:00'),
      toDate: format(endDate, 'yyyy-MM-dd 23:59:59'),
    });

    dataHistoryEmptyEndDateRes = dataHistoryEmptyEndDateRes ?? [];
    dataHistoryRes = dataHistoryRes.concat(dataHistoryEmptyEndDateRes);


    // Load icon images for history data as well
    const statusIconList = dataHistoryRes
      .filter(item => item.icon && item.icon !== 'default')
      .map(item => item.icon);

    const statusIconBase64Map = {};

    try {
      if (statusIconList.length > 0) {
        const iconUrls = await fileService.getFileUrls(statusIconList);

        statusIconList.forEach(icon => {
          statusIconBase64Map[icon] = iconUrls[icon] || 'default';
        });
      }
    } catch (error) {
      console.error('Failed to load status icons:', error);
      for (const statusIcon of statusIconList) {
        try {
          const statusIconBase64 = await fileService.getFileUrl(statusIcon);
          statusIconBase64Map[statusIcon] = statusIconBase64;
        } catch (iconError) {
          console.error(`Failed to load icon ${statusIcon}:`, iconError);
          statusIconBase64Map[statusIcon] = 'default';
        }
      }
    }

    // Add iconImage to history data table
    const updatedDataTableHistory = dataHistoryRes.map(item => ({
      ...item,
      iconImage: statusIconBase64Map[item.icon] ?? 'default',
    }));

    this.setState({
      dataTableHistory: cloneDeep(updatedDataTableHistory),
      isLoadingHistory: false,
    });
  };

  renderTooltipContent(tooltipData) {
    const { start_time, end_time, status, device_name } = tooltipData;
    const startTime = format(new Date(start_time), 'MM月dd日 - HH:mm');

    const endTime = end_time
      ? format(new Date(end_time), 'MM月dd日 - HH:mm')
      : '';

    return (
      <div className="tooltip-content device-status-overview-history">
        <div className="tooltip-content-item timestamp">
          <b>{`${startTime} ~ ${endTime}`}</b>
        </div>
        <div className="tooltip-content-item">
          <div style={{ whiteSpace: 'nowrap' }}>{COMMON_TEXT.DEVICE_NAME}</div>
          <b>{`${device_name}`}</b>
        </div>
        <div className="tooltip-content-item">
          <div style={{ whiteSpace: 'nowrap' }}>{COMMON_TEXT.STATUS}</div>
          <b className={`status status-${status}`}>{`${status}`}</b>
        </div>
      </div>
    );
  }

  renderOverlayPanelContent = () => {
    const { selectedDevice } = this.state;
    const { status_tooltip = [] } = selectedDevice;

    const getTimeLabel = ({ item }) => {
      let timeLabel = '';
      const { start_time: startTime, end_time: endTime } = item;

      if (startTime) {
        const startDateTime = new Date(startTime);
        timeLabel = format(startDateTime, 'yyyy年MM月dd日 - HH:mm');
      }

      if (startTime && endTime) {
        timeLabel = `${timeLabel} ~ `;
      }

      if (
        startTime &&
        endTime &&
        isSameDay(new Date(startTime), new Date(endTime))
      ) {
        const endDateTime = new Date(endTime);
        timeLabel = `${timeLabel}${format(endDateTime, 'HH:mm')}`;
      }

      if (
        startTime &&
        endTime &&
        !isSameDay(new Date(startTime), new Date(endTime))
      ) {
        if (endTime) {
          const endDateTime = new Date(endTime);
          timeLabel = `${timeLabel}${format(
            endDateTime,
            'yyyy年MM月dd日 - HH:mm'
          )}`;
        }

        if (!startTime && !endTime) {
          timeLabel = '';
        }
      }
      return timeLabel;
    };

    return (
      <>
        {status_tooltip.map((item, index) => {
          const timeLabel = getTimeLabel({ item });
          return (
            <div className="row-item" key={`row-item-idx-${index}`}>
              <div className="time-item">{`${timeLabel}`}</div>
              <div className={`status-item status-${item.status}`}>{`${item.status ?? ''
                }`}</div>
            </div>
          );
        })}
      </>
    );
  };

  renderDataTableHistory = () => {
    const { dataTableHistory, isLoadingHistory } = this.state;

    const dataByDeviceName = sortObjectKeys(
      convertArrayToObject(dataTableHistory, 'device_name')
    );

    const statusBodyTemplate = (dataItem, col) => {
      const { iconImage, status, product } = dataItem;

      var defaultIcon = '/assets/img/verkada-camera-icon.svg';
      if (product === 'Meraki') {
        defaultIcon = '/assets/img/meraki-wifi-icon.svg';
      }

      return (
        <div style={{ textAlign: 'left', display: 'flex', justifyContent: 'flex-start' }}>
          <span className="p-column-title">{col.header}</span>
          {iconImage && iconImage !== 'default' ? (
            <img
              src={iconImage}
              alt="device"
              width="24"
              height="24"
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <div
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: status === 'offline' ? '#FF6E6E' : '#6BCE7A',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: '#666',
                border: '1px solid #fff'
              }}
            >
              <img
                src={defaultIcon}
                alt="device"
                width="15"
                height="15"
                style={{ objectFit: 'contain' }}
              />
            </div>
          )}
        </div>
      );
    };

    const productBodyTemplate = (dataItem, col) => {
      const value = dataItem[col.field];
      return (
        <div style={{ textAlign: 'left' }}>
          <span className="p-column-title">{value}</span>
        </div>
      );
    };

    const typeBodyTemplate = (dataItem, col) => {
      const value = dataItem[col.field];
      return (
        <div style={{ textAlign: 'left' }}>
          <span className="p-column-title">{value}</span>
        </div>
      );
    };

    const deviceNameOnClicked = (e, data) => {
      this.setState({ selectedDevice: data });
      this.overlayRef.current.toggle(e);
    };

    const nameBodyTemplate = (dataItem, col) => {
      const value = dataItem[col.field];
      return (
        <div style={{ textAlign: 'left' }}>
          <span
            className="p-column-title device_name"
            onClick={e => deviceNameOnClicked(e, dataItem)}
          >
            {value}
          </span>
        </div>
      );
    };

    const dateBodyTemplate = (dataItem, col) => {
      const value = dataItem[col.field];
      if (!value) return '-';
      return (
        <div style={{ textAlign: 'left' }}>
          <span className="p-column-title">{format(new Date(value), 'yyyy-MM-dd HH:mm')}</span>
        </div>
      );
    };

    let groupedData = {};

    for (let key in dataByDeviceName) {
      if (dataByDeviceName.hasOwnProperty(key)) {
        const data = dataByDeviceName[key];
        const deviceName = key;

        data.forEach(item => {
          if (!groupedData[deviceName]) {
            groupedData[deviceName] = {
              ...item,
              device_name: deviceName,
              status: item.status,
              status_tooltip: [
                {
                  start_time: item.start_time,
                  end_time: item.end_time,
                  status: item.status,
                  device_name: deviceName,
                },
              ],
              last_error_timestamp:
                item.status === 'offline' ? item.end_time : null,
            };
          } else {
            groupedData[deviceName].status_tooltip.push({
              start_time: item.start_time,
              end_time: item.end_time,
              status: item.status,
              device_name: deviceName,
            });
            if (
              item.status === 'offline' &&
              (groupedData[deviceName].last_error_timestamp === null ||
                new Date(item.end_time) >
                new Date(groupedData[deviceName].last_error_timestamp))
            ) {
              groupedData[deviceName].last_error_timestamp = item.end_time;
            }
            if (
              item.end_time === null ||
              new Date(item.end_time) >
              new Date(groupedData[deviceName].end_time)
            ) {
              groupedData[deviceName].status = item.status;
            }
          }
        });
      }
    }

    let dataHistoryAfterGroup = Object.values(groupedData);

    return (
      <DataTable
        ref={this.dataHistoryTableRef}
        className="history-table"
        value={dataHistoryAfterGroup}
        tableStyle={{ width: '100%', backgroundColor: '#252f55' }}
        scrollable
        // scrollHeight="flex"
        // scrollHeight="calc(100vh - 200px)"
        loading={isLoadingHistory}
        emptyMessage={() => {
          return (
            <div className="flex justify-content-center align-items-center" style={{ padding: '20px' }}>
              <div className="no-data-container">
                {COMMON_TEXT.NO_DATA}
              </div>
            </div>
          );
        }}
      >
        <Column
          field="status"
          header="状態"
          body={statusBodyTemplate}
          className="status-column"
          headerStyle={{ textAlign: 'left', backgroundColor: '#252f55', color: '#A2A8BB', borderColor: '#505680' }}
          style={{ width: '8%', backgroundColor: '#252f55', color: '#FFFFFF', borderColor: '#505680', textAlign: 'left' }}
        />
        <Column
          field="product"
          header="プロダクト"
          body={productBodyTemplate}
          headerStyle={{ textAlign: 'left', backgroundColor: '#252f55', color: '#A2A8BB', borderColor: '#505680' }}
          style={{ width: '12%', backgroundColor: '#252f55', color: '#FFFFFF', borderColor: '#505680', textAlign: 'left' }}
        />
        <Column
          field="device_type"
          header="機種"
          body={typeBodyTemplate}
          headerStyle={{ textAlign: 'left', backgroundColor: '#252f55', color: '#A2A8BB', borderColor: '#505680' }}
          style={{ width: '8%', backgroundColor: '#252f55', color: '#FFFFFF', borderColor: '#505680', textAlign: 'left' }}
        />
        <Column
          field="device_name"
          header="名前"
          body={nameBodyTemplate}
          headerStyle={{ textAlign: 'left', backgroundColor: '#252f55', color: '#A2A8BB', borderColor: '#505680' }}
          style={{ width: '20%', backgroundColor: '#252f55', color: '#FFFFFF', borderColor: '#505680', textAlign: 'left' }}
        />
        <Column
          field="end_time"
          header="発生日時"
          body={dateBodyTemplate}
          headerStyle={{ textAlign: 'left', backgroundColor: '#252f55', color: '#A2A8BB', borderColor: '#505680' }}
          style={{ width: '12%', backgroundColor: '#252f55', color: '#FFFFFF', borderColor: '#505680', textAlign: 'left' }}
        />
        <Column
          field="status_history"
          header="状態履歴"
          body={(dataItem) => this.renderStatusBar(dataItem)}
          headerStyle={{ textAlign: 'left', backgroundColor: '#252f55', color: '#A2A8BB', borderColor: '#505680' }}
          style={{ width: '40%', backgroundColor: '#252f55', color: '#FFFFFF', borderColor: '#505680', textAlign: 'left' }}
        />
      </DataTable>
    );
  };

  getPercentOfTimeInHours = (fromTime, toTime) => {
    const { startDate, endDate } = this.state;
    const totalTime = endDate.getTime() - startDate.getTime();
    const diffTime = toTime.getTime() - fromTime.getTime();
    const percent = (diffTime / totalTime) * 100;
    return percent;
  };

  getPercentOfOffsetInHours = fromTime => {
    const { startDate, endDate } = this.state;
    const totalTime = endDate.getTime() - startDate.getTime();
    const diffTime = fromTime.getTime() - startDate.getTime();
    const percent = (diffTime / totalTime) * 100;
    return percent;
  };

  renderStatusBar = dataItem => {
    const { startDate, endDate } = this.state;
    const statusTooltip = dataItem.status_tooltip ?? [];
    const hasData = statusTooltip.length > 0;
    const now = new Date();
    const widthAfterEnd = now.getTime() < endDate.getTime() ?
      this.getPercentOfTimeInHours(now, endDate) : 0;

    return (
      <div className="status-container" style={{ textAlign: 'left' }}>
        {hasData && statusTooltip.map((item, index) => {
          const { start_time, end_time, status } = item;
          // Create CSS-safe ID by removing/replacing invalid characters
          const safeDeviceName = (dataItem.device_name || '').replace(/[^a-zA-Z0-9]/g, '-');
          const safeStartTime = start_time ? new Date(start_time).getTime().toString() : 'no-start';
          const targetId = `${safeDeviceName}-${index}-${safeStartTime}`;

          let fromTime = new Date(start_time);
          if (fromTime.getTime() < startDate.getTime()) {
            fromTime = startDate;
          }

          let toTime = end_time ? new Date(end_time) : now;
          if (toTime.getTime() > endDate.getTime()) {
            toTime = endDate;
          }

          const width = this.getPercentOfTimeInHours(fromTime, toTime);
          const left = this.getPercentOfOffsetInHours(fromTime);

          const childDiv = (
            <Fragment key={`child-div-${targetId}`}>
              <Tooltip
                className="child-div-tooltip"
                target={`.child-div-tooltip-target-${targetId}`}
                baseZIndex={2000}
                autoHide={false}
                position="top"
                event="hover"
              >
                {this.renderTooltipContent(item)}
              </Tooltip>
              <div
                className={`child-div child-div-tooltip-target-${targetId} status-${status}`}
                style={{
                  width: `${width}%`,
                  left: `${left}%`,
                }}
              />
            </Fragment>
          );

          return childDiv;
        })}
        <div
          className="child-div status-no-data"
          style={{
            width: `${widthAfterEnd}%`,
            right: 0,
          }}
        />
      </div>
    );
  };

  render() {
    const { isLoadingHistory, startDate, endDate, selectedDevice } = this.state;
    const maxDate = new Date();

    return (
      <Fragment>
        <div className="data-panel">
          <div className="panel-header">
            <div className="date-controls">
              <div className='date-picker-container'>
                <CustomCalendar
                  value={startDate}
                  onChange={value => this.datePickerOnChanged(value, 'startDate')}
                  maxDate={maxDate}
                  label="開始日付"
                />
              </div>

              <div className='date-picker-container'>
                <CustomCalendar
                  value={endDate}
                  onChange={value => this.datePickerOnChanged(value, 'endDate')}
                  maxDate={maxDate}
                  label="終了日付"
                />
              </div>
            </div>
          </div>
          <div className="table-container">
            {isLoadingHistory ? (
              <LoadingSpinner />
            ) : (
              <>
                <OverlayPanel
                  className="overlay-panel-device-history"
                  ref={this.overlayRef}
                  showCloseIcon
                  closeOnEscape
                  dismissable={true}
                >
                  <div className="overlay-panel-content-container">
                    <h6>{selectedDevice.device_name}</h6>
                    <div className="overlay-panel-content-container-items">
                      {this.renderOverlayPanelContent()}
                    </div>
                  </div>
                </OverlayPanel>
                {this.renderDataTableHistory()}
              </>
            )}
          </div>
        </div>
      </Fragment>
    );
  }
}

DeviceStatusOverviewHistory.propTypes = {};

export default DeviceStatusOverviewHistory;
